import { drawFrame, mapToPlot, drawSpatialFieldRgb } from './canvasUtils';

export const fhn0D = {
    init: () => ({ v: -1.0, w: -0.5, t: 0, history: [] }),
    step: (ctx, w, h, state, params) => {
        const dt = 0.02;
        const I = params.stimulus;
        const dv = state.v - (state.v ** 3) / 3 - state.w + I;
        const dw = (state.v + params.a - params.b * state.w) / params.tau;
        state.v += dv * dt;
        state.w += dw * dt;
        state.t += dt;
        state.history.push({ t: state.t, v: state.v });
        if (state.history.length > 500) state.history.shift();

        const area = drawFrame(ctx, w, h, {
            xLabel: 't (time)',
            yLabel: 'v (voltage)',
            title: 'FitzHugh-Nagumo trace',
            t: state.t,
        });

        const tMin = state.history[0]?.t ?? 0;
        const tMax = state.t || tMin + 1;
        const vMin = -2;
        const vMax = 2;

        ctx.strokeStyle = '#ef4743';
        ctx.lineWidth = 2;
        ctx.beginPath();
        state.history.forEach((pt, i) => {
            const mapped = mapToPlot(area, pt.t, pt.v, tMin, tMax, vMin, vMax);
            if (i === 0) ctx.moveTo(mapped.x, mapped.y);
            else ctx.lineTo(mapped.x, mapped.y);
        });
        ctx.stroke();
    },
};

export const fhn2D = {
    init: (w) => {
        const n = 90;
        const v = new Float32Array(n * n);
        const rec = new Float32Array(n * n);
        for (let y = 0; y < n; y++) {
            for (let x = 0; x < n; x++) {
                v[y * n + x] = x < n / 2 ? 1.0 : -1.0;
                rec[y * n + x] = y < n / 2 ? 0.5 : -0.5;
            }
        }
        return { n, v, rec, t: 0 };
    },
    step: (ctx, w, h, state, params) => {
        const { n, v, rec } = state;
        const nextV = new Float32Array(n * n);
        const nextRec = new Float32Array(n * n);
        const D = params.diffusion2d;
        for (let y = 0; y < n; y++) {
            for (let x = 0; x < n; x++) {
                const idx = y * n + x;
                const lap =
                    v[y * n + ((x - 1 + n) % n)] +
                    v[y * n + ((x + 1) % n)] +
                    v[((y - 1 + n) % n) * n + x] +
                    v[((y + 1) % n) * n + x] -
                    4 * v[idx];
                const dv = v[idx] - (v[idx] ** 3) / 3 - rec[idx] + params.stimulus + D * lap;
                const dw = (v[idx] + params.a - params.b * rec[idx]) / params.tau;
                nextV[idx] = v[idx] + 0.05 * dv;
                nextRec[idx] = rec[idx] + 0.05 * dw;
            }
        }
        state.v = nextV;
        state.rec = nextRec;
        state.t += 0.05;

        drawSpatialFieldRgb(ctx, w, h, n, (idx) => {
            const val = nextV[idx];
            const t01 = Math.max(0, Math.min(1, (val + 2) / 4));
            const g = Math.floor(t01 * 220);
            return `rgb(${255 - g},${g},80)`;
        }, {
            xLabel: 'x (space)',
            yLabel: 'y (space)',
            title: 'Excitation field v(x, y, t)',
            t: state.t,
        });
    },
};

export const fhnStochastic = {
    init: () => ({ v: -1.2, w: -0.6, t: 0, history: [] }),
    step: (ctx, w, h, state, params) => {
        const dt = 0.02;
        const I = params.stimulus;
        const noise = params.noise * (Math.random() - 0.5);
        const dv = state.v - (state.v ** 3) / 3 - state.w + I + noise;
        const dw = (state.v + params.a - params.b * state.w) / params.tau;
        state.v += dv * dt;
        state.w += dw * dt;
        state.t += dt;
        state.history.push({ t: state.t, v: state.v });
        if (state.history.length > 500) state.history.shift();

        const area = drawFrame(ctx, w, h, {
            xLabel: 't (time)',
            yLabel: 'v (voltage)',
            title: 'Noise-induced spiking',
            t: state.t,
        });

        const tMin = state.history[0]?.t ?? 0;
        const tMax = state.t || tMin + 1;

        ctx.strokeStyle = '#4fc3f7';
        ctx.lineWidth = 2;
        ctx.beginPath();
        state.history.forEach((pt, i) => {
            const mapped = mapToPlot(area, pt.t, pt.v, tMin, tMax, -2, 2);
            if (i === 0) ctx.moveTo(mapped.x, mapped.y);
            else ctx.lineTo(mapped.x, mapped.y);
        });
        ctx.stroke();
    },
};
